import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import { useAuthStore } from '@/stores/authStore';
import type { AuthUser } from '@/stores/authStore';
import { meRequest } from '@/api/auth';

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Bootstraps the auth state on app load. Strategy:
 *   1. Try /auth/me with a silent /auth/refresh first — this restores the session
 *      if a valid refresh cookie exists.
 *   2. If that fails, user stays logged out.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const { user, setAccessToken, setUser, clear } = useAuthStore();
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const me = await meRequest();
      setUser(me);
    } catch {
      clear();
    }
  }, [setUser, clear]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // If axios client already has a token (e.g. test seed), try /me directly.
      if (useAuthStore.getState().accessToken) {
        try {
          const me = await meRequest();
          if (!cancelled) setUser(me);
        } catch {
          if (!cancelled) clear();
        } finally {
          if (!cancelled) setLoading(false);
        }
        return;
      }
      // No token in memory → try to get one via refresh cookie.
      try {
        const res = await fetch('/api/auth/refresh', {
          method: 'POST',
          credentials: 'include',
        });
        if (res.ok) {
          const json = (await res.json()) as { data: { accessToken: string; user: AuthUser } };
          if (!cancelled) {
            setAccessToken(json.data.accessToken);
            setUser(json.data.user);
          }
        }
      } catch {
        // ignore — stay logged out
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [setAccessToken, setUser, clear]);

  const value: AuthContextValue = {
    user: user ?? null,
    loading,
    refresh,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
